import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRightIcon } from '@heroicons/react/24/solid'
import { slideFromLeft, slideFromRight } from '@/lib/animations'

const PROMISES = [
  'I design Illuminate to send you back to your Bible, your pastor, and your church — never to replace them.',
  'You own your prayers, journals, and chats. They’re never sold, and you can delete every one of them, anytime.',
  'Every email reaches me, not a help desk. That doesn’t change as we grow.',
]

const ROMAN_NUMERALS = ['I', 'II', 'III']

// Small inline cross used as a typographic ornament between the rule
// segments at the top and bottom of the promises panel. Hand-drawn-feel
// (slight stroke variation), not megachurch-slide clipart.
function CrossOrnament({ size = 14, color = '#c9a030' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M7 1.5v11" stroke={color} strokeWidth="1" strokeLinecap="round" />
      <path d="M1.5 7h11" stroke={color} strokeWidth="1" strokeLinecap="round" />
    </svg>
  )
}

export default function MissionFounder() {
  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: '#faf5e8' }}>
      {/* Golden light texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/golden-light.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.06,
          mixBlendMode: 'multiply',
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 py-20 md:py-28">
        {/* Section heading */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.1]"
            style={{ color: '#1a0f00' }}
          >
            The Heart Behind <span style={{ color: '#c9a030' }}>Illuminate</span>
          </h2>
        </motion.div>

        {/* Founder section — 2-column grid (photo left, text right). The
            grid is constrained to the SAME max-width as the promises
            panel below (max-w-2xl on mobile, max-w-4xl on desktop) so the
            left edge of the photo aligns with the left edge of the
            promises panel, and the right edge of the text aligns with
            the right edge of the promises panel. Photo column uses
            justify-start so the photo sits flush at the left edge. */}
        <motion.div
          className="grid md:grid-cols-2 gap-12 md:gap-16 items-center max-w-2xl md:max-w-4xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          {/* Photo — larger, thicker gold frame, bigger accent block.
              Sits flush at the LEFT edge of its column on desktop so it
              aligns with the promises panel below. */}
          <motion.div variants={slideFromLeft} className="flex justify-center md:justify-start">
            <div className="relative">
              <div
                className="w-72 h-96 md:w-96 md:h-[32rem] rounded-2xl overflow-hidden shadow-xl"
                style={{ border: '4px solid rgba(201,160,48,0.4)' }}
              >
                <img
                  src="/founder.png"
                  alt="Mason — Founder of Illuminate Faith"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Decorative accent — bigger, more present */}
              <div
                className="absolute -bottom-5 -right-5 md:-bottom-6 md:-right-6 w-28 h-28 md:w-36 md:h-36 rounded-xl -z-10"
                style={{ backgroundColor: 'rgba(201,160,48,0.2)' }}
              />
            </div>
          </motion.div>

          {/* Story — left-aligned in its column */}
          <motion.div variants={slideFromRight}>
            <h3
              className="font-heading text-2xl md:text-3xl font-extrabold leading-tight mb-4"
              style={{ color: '#1a0f00' }}
            >
              Who I Am
            </h3>
            <div
              className="space-y-4 text-sm md:text-base leading-relaxed"
              style={{ color: '#3d2e1e' }}
            >
              <p>
                I'm Mason — a college student at Texas A&M who loves apologetics, gives sermons
                regularly, and couldn't find an AI tool that actually takes Scripture seriously. So
                I built one.
              </p>
              <p>
                Illuminate exists because Jesus is the most important thing — and everything I
                build, I want to glorify Him. This tool is for every Christian who wants to go
                deeper in the Word, defend what they believe, and grow in their walk with God.
                Whether you're a student, a pastor, or just someone who wants to know Scripture
                better — this is for you.
              </p>
            </div>
            <blockquote className="mt-6 pl-4 py-2" style={{ borderLeft: '3px solid #c9a030' }}>
              <p className="font-heading italic text-sm md:text-base" style={{ color: '#5a4a38' }}>
                &ldquo;But he said to me, &lsquo;My grace is sufficient for you, for my power is
                made perfect in weakness.&rsquo; Therefore I will boast all the more gladly about my
                weaknesses, so that Christ&apos;s power may rest on me.&rdquo;
              </p>
              <cite
                className="text-xs font-medium not-italic tracking-wider uppercase mt-1 block"
                style={{ color: '#c9a030' }}
              >
                2 Corinthians 12:9 (NIV)
              </cite>
            </blockquote>
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold hover:scale-105 transition-all duration-200"
              style={{ color: '#c9a030' }}
            >
              Read my full story <ArrowRightIcon className="w-4 h-4" aria-hidden="true" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Promises panel — signed personal declaration. Wrapped in a
            paper-feel container with multi-layer shadow, thin inset gold
            frame, and centered numbered clauses, so the panel reads as a
            printed document grounded on the page rather than floating text.
            Hardcoded parchment palette per DESIGN.md §7.5. */}
        <motion.div
          className="mt-20 md:mt-24 max-w-2xl md:max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          {/* Paper-feel container — the "3D element behind" the text. The
              lighter cream bg + layered shadows + thin inner border give
              it real surface depth without becoming a card cliche. */}
          <div
            className="relative"
            style={{
              backgroundColor: '#fffaef',
              boxShadow:
                '0 1px 2px rgba(20,15,0,0.05), 0 12px 32px rgba(20,15,0,0.08), 0 32px 80px rgba(20,15,0,0.05)',
              borderRadius: '2px',
            }}
          >
            {/* Inset gold frame — the "printed on the page" border. Very
                subtle so it doesn't read as a Certificate of Achievement. */}
            <div
              className="absolute pointer-events-none"
              style={{
                inset: '12px',
                border: '1px solid rgba(201,160,48,0.22)',
                borderRadius: '1px',
              }}
              aria-hidden="true"
            />

            <div className="relative px-6 py-12 md:px-14 md:py-16">
              {/* Top ornamental rule + cross — inside the paper */}
              <div
                className="flex items-center justify-center gap-3 md:gap-4 mb-8 md:mb-10"
                aria-hidden="true"
              >
                <span className="h-px w-12 md:w-20" style={{ backgroundColor: '#c9a030' }} />
                <CrossOrnament size={14} />
                <span className="h-px w-12 md:w-20" style={{ backgroundColor: '#c9a030' }} />
              </div>

              {/* Heading */}
              <h3
                className="font-heading text-2xl md:text-3xl font-extrabold text-center leading-tight mb-10 md:mb-14"
                style={{ color: '#1a0f00' }}
              >
                My Promises to You
              </h3>

              {/* The promises — single column on mobile, 3-column row on
                  desktop (one clause per column) so 3 promises fill the
                  wider container in a single balanced row. Each clause
                  centers its numeral + body text within its column. */}
              <ol className="grid grid-cols-1 md:grid-cols-3 gap-y-10 md:gap-y-0 md:gap-x-8">
                {PROMISES.map((promise, i) => (
                  <motion.li
                    key={i}
                    className="text-center"
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: i * 0.12, duration: 0.6, ease: 'easeOut' }}
                  >
                    {/* Numeral with flanking short rules — chapter-marker
                        style. Sits above each promise body. */}
                    <div className="flex items-center justify-center gap-3 mb-3" aria-hidden="true">
                      <span
                        className="h-px w-7"
                        style={{ backgroundColor: 'rgba(201,160,48,0.55)' }}
                      />
                      <span
                        className="italic leading-none text-lg md:text-xl"
                        style={{
                          fontFamily: 'Newsreader, Georgia, serif',
                          color: '#c9a030',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {ROMAN_NUMERALS[i]}
                      </span>
                      <span
                        className="h-px w-7"
                        style={{ backgroundColor: 'rgba(201,160,48,0.55)' }}
                      />
                    </div>

                    {/* Promise body — Newsreader serif, centered, max-width
                        constrained so 1-2 line promises don't sprawl. */}
                    <p
                      className="text-base md:text-lg leading-relaxed max-w-md mx-auto"
                      style={{
                        fontFamily: 'Newsreader, Georgia, serif',
                        color: '#1a0f00',
                      }}
                    >
                      {promise}
                    </p>
                  </motion.li>
                ))}
              </ol>

              {/* Bottom ornamental rule + cross — closes the document */}
              <div
                className="flex items-center justify-center gap-3 md:gap-4 mt-10 md:mt-12"
                aria-hidden="true"
              >
                <span className="h-px w-12 md:w-20" style={{ backgroundColor: '#c9a030' }} />
                <CrossOrnament size={14} />
                <span className="h-px w-12 md:w-20" style={{ backgroundColor: '#c9a030' }} />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
